import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../style.css';
import { AuthContext } from '../contexts/authcontext';

/* ── Character colour palette ── */
const characters = [
  { id: 1, name: 'Bubbles', skin: '#ffd8b5', skinDark: '#e8a87c', hair: '#c0392b', hairDark: '#7b241c', outfit: '#ff6b9d', outfitDark: '#c2185b', iris: '#1565C0' },
  { id: 2, name: 'Pippin',  skin: '#ffe0f5', skinDark: '#f9b8d8', hair: '#8e44ad', hairDark: '#5b2c6f', outfit: '#ce93d8', outfitDark: '#9c27b0', iris: '#7b1fa2' },
  { id: 3, name: 'Sunny',   skin: '#fff9c4', skinDark: '#ffe082', hair: '#e67e22', hairDark: '#935116', outfit: '#fdd835', outfitDark: '#f9a825', iris: '#e65100' },
  { id: 4, name: 'Mochi',   skin: '#c8f5e8', skinDark: '#9ee8cb', hair: '#16a085', hairDark: '#0a5c48', outfit: '#80cbc4', outfitDark: '#00796b', iris: '#00695c' },
];

/* ── Hair shapes per character ── */
function HairBack({ char }) {
  const g = `url(#hair-${char.id})`;
  if (char.id === 1) return (
    <>
      <ellipse cx="50" cy="22" rx="31" ry="20" fill={g} />
      <ellipse cx="16" cy="44" rx="9" ry="13" fill={g} />
      <ellipse cx="84" cy="44" rx="9" ry="13" fill={g} />
    </>
  );
  if (char.id === 2) return (
    <>
      <ellipse cx="50" cy="22" rx="31" ry="20" fill={g} />
      <ellipse cx="80" cy="30" rx="16" ry="16" fill={g} />
    </>
  );
  if (char.id === 3) return (
    <>
      <ellipse cx="50" cy="22" rx="31" ry="20" fill={g} />
      <path d="M30 14 L26 2 L36 12 L38 0 L45 12 L50 0 L55 12 L62 0 L64 12 L74 2 L70 14" fill={g} />
    </>
  );
  return <ellipse cx="50" cy="24" rx="33" ry="22" fill={g} />;
}

function HairFront({ char }) {
  const g = `url(#hair-${char.id})`;
  const stroke = char.hair;
  if (char.id === 1) return (
    <>
      <path d="M19 35 Q18 14 50 10 Q82 14 81 35" fill={g} />
      <path d="M19 34 Q14 42 18 52" stroke={stroke} strokeWidth="9" fill="none" strokeLinecap="round" />
      <path d="M81 34 Q86 42 82 52" stroke={stroke} strokeWidth="9" fill="none" strokeLinecap="round" />
      <ellipse cx="14" cy="46" rx="6" ry="5" fill="#ff6b9d" />
      <ellipse cx="86" cy="46" rx="6" ry="5" fill="#ff6b9d" />
    </>
  );
  if (char.id === 2) return (
    <>
      <path d="M19 35 Q18 14 50 10 Q82 14 81 35" fill={g} />
      <path d="M50 10 Q65 12 78 22" stroke={stroke} strokeWidth="7" fill="none" strokeLinecap="round" />
      <path d="M81 34 Q86 42 82 52" stroke={stroke} strokeWidth="8" fill="none" strokeLinecap="round" />
    </>
  );
  if (char.id === 3) return (
    <>
      <path d="M19 35 Q18 14 50 10 Q82 14 81 35" fill={g} />
      <path d="M27 28 L22 14 L32 24" fill={g} />
      <path d="M38 18 L36 4 L44 16" fill={g} />
      <path d="M50 14 L50 2 L56 14" fill={g} />
      <path d="M62 18 L64 4 L70 16" fill={g} />
    </>
  );
  return (
    <>
      <path d="M17 38 Q16 10 50 8 Q84 10 83 38" fill={g} />
      <rect x="18" y="24" width="64" height="14" rx="4" fill={g} />
      <path d="M18 38 Q14 48 17 60" stroke={stroke} strokeWidth="10" fill="none" strokeLinecap="round" />
      <path d="M82 38 Q86 48 83 60" stroke={stroke} strokeWidth="10" fill="none" strokeLinecap="round" />
    </>
  );
}

/* ── Single character SVG ── */
function CharacterSVG({ char, mood, activeField, isMobile }) {
  const eyeOffsetX = (mood === 'look' && activeField === 'email' && !isMobile) ? 3.5 : 0;
  const eyeOffsetY = (mood === 'look' && activeField === 'email' && isMobile) ? 4.5 : 0;
  const coverArms  = (mood === 'cover' && activeField === 'password');

  const eyeRY = mood === 'shocked' ? 11 : mood === 'happy' ? 1.2 : 9.5;
  const eyeRX = 8.5;

  const Mouth = () => {
    if (mood === 'happy')    return <path d="M34 56 Q50 70 66 56" stroke="#c2185b" strokeWidth="3" fill="rgba(255,100,120,0.18)" strokeLinecap="round" />;
    if (mood === 'shocked')  return <ellipse cx="50" cy="60" rx="9" ry="11" fill="rgba(180,40,40,0.18)" stroke="#c0392b" strokeWidth="2.2" />;
    if (mood === 'thinking') return <path d="M42 58 Q50 62 55 57" stroke="#555" strokeWidth="2" fill="none" strokeLinecap="round" />;
    return <path d="M39 57 Q50 64 61 57" stroke="#c0392b" strokeWidth="2" fill="none" strokeLinecap="round" />;
  };

  const lBrow = mood === 'shocked' ? "M34 32 Q38 27 44 31" : mood === 'happy' ? "M34 35 Q38 31 44 34" : "M34 35 Q38 32 44 34";
  const rBrow = mood === 'shocked' ? "M56 31 Q62 27 66 32" : mood === 'happy' ? "M56 34 Q62 31 66 35" : "M56 34 Q62 32 66 35";

  const id = char.id;

  return (
    <svg viewBox="0 0 100 160" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: 'visible' }} className={`char-svg mood-${mood}`}>
      <defs>
        <radialGradient id={`skin-${id}`} cx="36%" cy="28%" r="65%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
          <stop offset="42%" stopColor={char.skin} />
          <stop offset="100%" stopColor={char.skinDark} />
        </radialGradient>
        <radialGradient id={`hair-${id}`} cx="30%" cy="25%" r="72%">
          <stop offset="0%" stopColor={char.hair} stopOpacity="0.8" />
          <stop offset="55%" stopColor={char.hair} />
          <stop offset="100%" stopColor={char.hairDark} />
        </radialGradient>
        <radialGradient id={`outfit-${id}`} cx="38%" cy="22%" r="70%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
          <stop offset="48%" stopColor={char.outfit} />
          <stop offset="100%" stopColor={char.outfitDark} />
        </radialGradient>
        <radialGradient id={`iris-${id}`} cx="30%" cy="25%" r="65%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.7)" />
          <stop offset="38%" stopColor={char.iris} />
          <stop offset="100%" stopColor={char.hairDark} />
        </radialGradient>
        <filter id={`drop-${id}`} x="-25%" y="-25%" width="150%" height="150%">
          <feDropShadow dx="0" dy="3" stdDeviation="3.5" floodColor="rgba(0,0,0,0.17)" />
        </filter>
      </defs>

      {/* Back arm (normal) */}
      {!coverArms && (
        <path d="M32 88 Q16 100 14 116" stroke={`url(#outfit-${id})`} strokeWidth="15" fill="none" strokeLinecap="round" />
      )}

      {/* Body */}
      <rect x="26" y="80" width="48" height="52" rx="16" ry="16"
        fill={`url(#outfit-${id})`} filter={`url(#drop-${id})`} />
      <ellipse cx="50" cy="86" rx="14" ry="5.5" fill="white" opacity="0.22" />
      <path d="M41 80 L50 92 L59 80" stroke="white" strokeWidth="2.8" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.65" />
      <circle cx="50" cy="102" r="2.2" fill="white" opacity="0.5" />
      <circle cx="50" cy="113" r="2.2" fill="white" opacity="0.4" />

      {/* Legs */}
      <rect x="36" y="128" width="12" height="18" rx="6" fill={char.skinDark} />
      <rect x="52" y="128" width="12" height="18" rx="6" fill={char.skinDark} />
      <ellipse cx="42" cy="148" rx="8" ry="5" fill={char.hairDark} />
      <ellipse cx="58" cy="148" rx="8" ry="5" fill={char.hairDark} />

      {/* Neck */}
      <rect x="42" y="68" width="16" height="15" rx="5" fill={char.skin} />
      <ellipse cx="50" cy="82" rx="8" ry="3" fill="rgba(0,0,0,0.07)" />

      {/* Hair back */}
      <HairBack char={char} />

      {/* Ears */}
      <ellipse cx="20" cy="42" rx="5.5" ry="7" fill={`url(#skin-${id})`} />
      <ellipse cx="80" cy="42" rx="5.5" ry="7" fill={`url(#skin-${id})`} />
      <ellipse cx="20" cy="42" rx="3" ry="4.5" fill={char.skinDark} opacity="0.5" />
      <ellipse cx="80" cy="42" rx="3" ry="4.5" fill={char.skinDark} opacity="0.5" />

      {/* Head */}
      <ellipse cx="50" cy="40" rx="32" ry="31"
        fill={`url(#skin-${id})`} filter={`url(#drop-${id})`} />
      <ellipse cx="36" cy="28" rx="11" ry="7" fill="white" opacity="0.2" transform="rotate(-20 36 28)" />

      {/* Hair front */}
      <HairFront char={char} />
      <path d="M32 12 Q50 7 64 14" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.40" />

      {/* Eyebrows */}
      <path d={lBrow} stroke={char.hairDark} strokeWidth="2.8" fill="none" strokeLinecap="round" />
      <path d={rBrow} stroke={char.hairDark} strokeWidth="2.8" fill="none" strokeLinecap="round" />

      {/* Eyes */}
      <g transform={`translate(${eyeOffsetX},${eyeOffsetY})`}>
        <g transform="translate(38,42)">
          <ellipse cx="0" cy="0" rx={eyeRX} ry={eyeRY} fill="white" />
          <ellipse cx="0.5" cy="1" rx={eyeRX * 0.62} ry={eyeRY * 0.62} fill={`url(#iris-${id})`} />
          <ellipse cx="0.5" cy="1" rx={eyeRX * 0.37} ry={eyeRY * 0.37} fill={char.hairDark} />
          <circle cx="2.5"  cy="-2" r="2.2" fill="white" opacity="0.95" />
          <circle cx="-2"   cy="2"  r="1.1" fill="white" opacity="0.6" />
          <line x1={-eyeRX} y1="0" x2={-eyeRX-2.5} y2="-3" stroke={char.hairDark} strokeWidth="1.4" strokeLinecap="round" />
          <line x1={-eyeRX*0.6} y1={-eyeRY*0.8} x2={-eyeRX*0.7} y2={-eyeRY-2} stroke={char.hairDark} strokeWidth="1.4" strokeLinecap="round" />
          <line x1="0" y1={-eyeRY} x2="0" y2={-eyeRY-2.5} stroke={char.hairDark} strokeWidth="1.4" strokeLinecap="round" />
          <line x1={eyeRX*0.6} y1={-eyeRY*0.8} x2={eyeRX*0.7} y2={-eyeRY-2} stroke={char.hairDark} strokeWidth="1.4" strokeLinecap="round" />
        </g>
        <g transform="translate(62,42)">
          <ellipse cx="0" cy="0" rx={eyeRX} ry={eyeRY} fill="white" />
          <ellipse cx="0.5" cy="1" rx={eyeRX * 0.62} ry={eyeRY * 0.62} fill={`url(#iris-${id})`} />
          <ellipse cx="0.5" cy="1" rx={eyeRX * 0.37} ry={eyeRY * 0.37} fill={char.hairDark} />
          <circle cx="2.5"  cy="-2" r="2.2" fill="white" opacity="0.95" />
          <circle cx="-2"   cy="2"  r="1.1" fill="white" opacity="0.6" />
          <line x1={-eyeRX} y1="0" x2={-eyeRX-2.5} y2="-3" stroke={char.hairDark} strokeWidth="1.4" strokeLinecap="round" />
          <line x1={-eyeRX*0.6} y1={-eyeRY*0.8} x2={-eyeRX*0.7} y2={-eyeRY-2} stroke={char.hairDark} strokeWidth="1.4" strokeLinecap="round" />
          <line x1="0" y1={-eyeRY} x2="0" y2={-eyeRY-2.5} stroke={char.hairDark} strokeWidth="1.4" strokeLinecap="round" />
          <line x1={eyeRX*0.6} y1={-eyeRY*0.8} x2={eyeRX*0.7} y2={-eyeRY-2} stroke={char.hairDark} strokeWidth="1.4" strokeLinecap="round" />
        </g>

        {/* Cover palms */}
        {coverArms && (
          <>
            <ellipse cx="38" cy="42" rx="14" ry="11" fill={`url(#skin-${id})`} filter={`url(#drop-${id})`} />
            <ellipse cx="62" cy="42" rx="14" ry="11" fill={`url(#skin-${id})`} filter={`url(#drop-${id})`} />
            <ellipse cx="38" cy="39" rx="8" ry="4.5" fill="white" opacity="0.18" />
            <ellipse cx="62" cy="39" rx="8" ry="4.5" fill="white" opacity="0.18" />
          </>
        )}
      </g>

      {/* Cheeks */}
      <ellipse cx="26" cy="52" rx="10" ry="7" fill="rgba(255,130,130,0.36)"
        opacity={(mood === 'cover' || mood === 'happy' || mood === 'shocked') ? 1 : 0.38} />
      <ellipse cx="74" cy="52" rx="10" ry="7" fill="rgba(255,130,130,0.36)"
        opacity={(mood === 'cover' || mood === 'happy' || mood === 'shocked') ? 1 : 0.38} />

      {/* Nose */}
      <path d="M47 52 Q50 55.5 53 52" stroke={char.skinDark} strokeWidth="2" fill="none" strokeLinecap="round" />

      {/* Mouth */}
      <Mouth />

      {/* Front arms (normal) */}
      {!coverArms && (
        <>
          <path d="M68 88 Q84 100 86 116" stroke={`url(#outfit-${id})`} strokeWidth="15" fill="none" strokeLinecap="round" className="arm-r" />
          <ellipse cx="87" cy="118" rx="7.5" ry="7.5" fill={`url(#skin-${id})`} filter={`url(#drop-${id})`} />
          <ellipse cx="14" cy="118" rx="7.5" ry="7.5" fill={`url(#skin-${id})`} filter={`url(#drop-${id})`} />
        </>
      )}

      {/* Cover arms raised */}
      {coverArms && (
        <>
          <path d="M28 85 Q20 68 28 52" stroke={`url(#outfit-${id})`} strokeWidth="15" fill="none" strokeLinecap="round" />
          <path d="M72 85 Q80 68 72 52" stroke={`url(#outfit-${id})`} strokeWidth="15" fill="none" strokeLinecap="round" />
        </>
      )}

      {/* Ground shadow */}
      <ellipse cx="50" cy="153" rx="26" ry="5" fill="rgba(0,0,0,0.09)" />
    </svg>
  );
}

/* ── Flowers ── */
function Flowers() {
  const defs = [
    { left: '7%',  bottom: '6%',  petals: '#f5f5f5', center: '#FFD600', petCount: 6, delay: '0s',   dur: '3.2s' },
    { left: '18%', bottom: '3%',  petals: '#DDA0DD', center: '#FFA000', petCount: 5, delay: '0.8s', dur: '2.8s' },
    { left: '31%', bottom: '7%',  petals: '#f5f5f5', center: '#FF8F00', petCount: 6, delay: '0.4s', dur: '3.6s' },
    { left: '57%', bottom: '4%',  petals: '#DDA0DD', center: '#FFD600', petCount: 5, delay: '1.2s', dur: '2.5s' },
    { left: '71%', bottom: '2%',  petals: '#fefce8', center: '#FFA000', petCount: 6, delay: '0.6s', dur: '3.1s' },
    { left: '84%', bottom: '5%',  petals: '#f5f5f5', center: '#FFD600', petCount: 5, delay: '1.5s', dur: '2.9s' },
    { left: '45%', bottom: '1%',  petals: '#DDA0DD', center: '#FF8F00', petCount: 6, delay: '0.2s', dur: '3.4s' },
  ];

  return (
    <div className="flowers-layer" aria-hidden="true">
      {defs.map((f, fi) => {
        const angles = Array.from({ length: f.petCount }, (_, i) => (360 / f.petCount) * i);
        return (
          <div key={fi} className="flower"
            style={{ left: f.left, bottom: f.bottom, animationDuration: f.dur, animationDelay: f.delay }}>
            <svg viewBox="-14 -14 28 28" width="26" height="26">
              {angles.map(deg => (
                <g key={deg} transform={`rotate(${deg})`}>
                  <ellipse cx="7" cy="0" rx="4.5" ry="2.8" fill={f.petals} opacity="0.92" />
                </g>
              ))}
              <circle cx="0" cy="0" r="4" fill={f.center} />
              <circle cx="-1.2" cy="-1.2" r="1.4" fill="rgba(255,255,255,0.5)" />
            </svg>
          </div>
        );
      })}
    </div>
  );
}

const DocLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarMood, setAvatarMood] = useState('playing');
  const [showPassword, setShowPassword] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const { doctorLogin } = useContext(AuthContext);
  const resetTimerRef = useRef(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const scheduleReset = useCallback(() => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setAvatarMood('playing');
      setActiveField(null);
    }, 3000);
  }, []);

  const validateForm = () => {
    if (email.trim() === '' || password.trim() === '') {
      toast.error('All fields are required.', {
        position: "top-center",
        draggable: true,
      });
      setAvatarMood('shocked');
      scheduleReset();
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Invalid email format.', {
        position: "top-center",
        draggable: true,
      });
      setAvatarMood('shocked');
      scheduleReset();
      return false;
    }
    return true;
  };

    const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    setAvatarMood('thinking');

    try {
      const result = await doctorLogin({
        email: email.trim().toLowerCase(),
        password
      });

      if (result.success) {
        setAvatarMood('happy');
        scheduleReset();
        const referrer = location.state?.referrer || '/dochome';
        navigate(referrer);
      } else {
        toast.error(result.error || 'Invalid email or password. Please try again.', {
          position: 'top-center',
          autoClose: result.isRateLimited ? 8000 : 5000
        });
        setAvatarMood('shocked');
        scheduleReset();
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.', { position: 'top-center' });
      setAvatarMood('shocked');
      scheduleReset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleEmailFocus = () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    setActiveField('email');
    setAvatarMood('look');
  };

  const handlePasswordFocus = () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    setActiveField('password');
    setAvatarMood('cover');
  };

  const handleBlur = () => {
    setActiveField(null);
    if (avatarMood === 'look' || avatarMood === 'cover') {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => setAvatarMood('playing'), 600);
    }
  };

  const handleCharacterClick = (name) => {
    const quips = {
      Bubbles: "Bubbles says: Welcome, Doctor! Ready to heal?",
      Pippin:  "Pippin says: Your clients are lucky to have you!",
      Sunny:   "Sunny says: Brighten someone's day today!",
      Mochi:   "Mochi says: Calm minds, healing hearts!",
    };
    toast.info(quips[name] ?? `${name} welcomes you, Doctor!`, {
      position: "top-center",
      autoClose: 2000,
      hideProgressBar: true,
    });
  };

  return (
    <div className="logbody">
      {/* ── Responsive Back Button ── */}
      <button 
        type="button" 
        className="back-button" 
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
        <span className="back-text">Back</span>
      </button>

      <div className="split-container">
        {/* Animation side with characters */}
        <div className="animation-side">
          <div className="xp-background" aria-hidden="true">
            {/* Sun */}
            <div className="xp-sun">
              <div className="sun-halo sun-halo-1" />
              <div className="sun-halo sun-halo-2" />
              <div className="sun-halo sun-halo-3" />
              <div className="sun-core" />
              <div className="sun-glare" />
            </div>

            {/* Clouds */}
            <div className="cloud xp-cloud-a">
              <div className="cb cb1" /><div className="cb cb2" /><div className="cb cb3" />
              <div className="cb cb4" /><div className="cb-main" />
            </div>
            <div className="cloud xp-cloud-b">
              <div className="cb cb1" /><div className="cb cb2" /><div className="cb cb3" />
              <div className="cb-main" />
            </div>
            <div className="cloud xp-cloud-c">
              <div className="cb cb1" /><div className="cb cb2" />
              <div className="cb-main" />
            </div>

            {/* Hills */}
            <svg className="xp-hills" viewBox="0 0 1200 400" preserveAspectRatio="xMidYMax slice" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="hg1" cx="50%" cy="0%" r="100%">
                  <stop offset="0%" stopColor="#33691e" /><stop offset="100%" stopColor="#1b5e20" />
                </radialGradient>
                <radialGradient id="hg2" cx="50%" cy="0%" r="100%">
                  <stop offset="0%" stopColor="#558b2f" /><stop offset="100%" stopColor="#33691e" />
                </radialGradient>
                <radialGradient id="hg3" cx="50%" cy="0%" r="100%">
                  <stop offset="0%" stopColor="#7cb342" /><stop offset="100%" stopColor="#558b2f" />
                </radialGradient>
                <radialGradient id="hg4" cx="50%" cy="0%" r="100%">
                  <stop offset="0%" stopColor="#aed561" /><stop offset="100%" stopColor="#8bc34a" />
                </radialGradient>
                <linearGradient id="hsheen" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.16)" />
                  <stop offset="55%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
              </defs>
              <path d="M-50 300 Q150 178 350 238 Q500 282 650 228 Q800 174 1000 218 Q1100 238 1260 198 L1260 400 L-50 400 Z" fill="url(#hg1)" />
              <path d="M-50 330 Q80 238 250 268 Q420 298 550 253 Q700 208 850 258 Q980 293 1100 248 Q1180 228 1260 238 L1260 400 L-50 400 Z" fill="url(#hg2)" />
              <path d="M-50 370 Q100 288 260 318 Q380 338 500 293 Q630 246 750 288 Q870 323 1000 298 Q1100 283 1260 308 L1260 400 L-50 400 Z" fill="url(#hg3)" />
              <path d="M-50 370 Q100 288 260 318 Q380 338 500 293 Q630 246 750 288 Q870 323 1000 298 Q1100 283 1260 308 L1260 400 L-50 400 Z" fill="url(#hsheen)" />
              <path d="M-50 390 Q200 348 380 370 Q550 388 700 358 Q850 336 1000 363 Q1100 376 1260 358 L1260 400 L-50 400 Z" fill="url(#hg4)" />
              <rect x="-50" y="398" width="1310" height="8" fill="#c5e1a5" />
            </svg>

            {/* Flowers */}
            <Flowers />
          </div>

          {/* Characters */}
          <div className="character-group">
            {characters.map((char, idx) => (
              <button
                key={char.id}
                className={`character-wrapper character-${idx + 1}`}
                onClick={() => handleCharacterClick(char.name)}
                title={`Click me! I'm ${char.name}!`}
                type="button"
              >
                <CharacterSVG
                  char={char}
                  mood={avatarMood}
                  activeField={activeField}
                  isMobile={isMobile}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Form side */}
        <div className="form-side">
          <div className="login-box">
           <div className="login-header">
  <div className="brand-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
    <img 
      src="/img/uxaahfavicon.PNG" 
      alt="Uxaah Logo" 
      style={{ width: '36px', height: '36px', objectFit: 'contain' }} 
    />
    <div className="login-logo" style={{ marginBottom: '0' }}>UXAAH</div>
  </div>
  <p className="login-subtitle">Welcome back</p>
</div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="input-group">
                <div className="input-wrap">
                  <span className="input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="3"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                    </svg>
                  </span>
                  <input
                    id="email-input"
                    type="email"
                    className="input-field"
                    placeholder="Professional Email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (activeField === 'email') setAvatarMood('look'); }}
                    onFocus={handleEmailFocus}
                    onBlur={handleBlur}
                    required
                  />
                </div>
              </div>

              <div className="input-group">
                <div className="input-wrap">
                  <span className="input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </span>
                  <input
                    id="password-input"
                    type={showPassword ? 'text' : 'password'}
                    className="input-field"
                    placeholder="Password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); if (activeField === 'password') setAvatarMood('cover'); }}
                    onFocus={handlePasswordFocus}
                    onBlur={handleBlur}
                    required
                  />
                  <button
                    type="button"
                    className="eye-toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="spinner-wrap"><span className="btn-spinner" /> Signing in…</span>
                ) : 'Sign In'}
              </button>
            </form>

            <div className="login-divider"><span>or</span></div>

            <div className="login-links">
              <p><a href="/forgot-password">Change password</a></p>
              <p><a href="/login">User Login</a></p>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer 
        position="top-center" 
        autoClose={5000} 
        theme="light" 
        pauseOnHover
      />
    </div>
  );
};

export default DocLogin;